import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Sidebar, { clearSession } from "../components/Sidebar";
import { applyBillingEnumText, normalizeActivationAccount } from "../utils/enums";
import { isPhaseAdmin } from "../utils/role";
import * as XLSX from "xlsx";
import {
  Dashboard6Styles, ModalShell, Field, PhoneInput, TITLE_OPTIONS, PagePreloader, StatusChip, Avatar, CardHead, PageHeader,
} from "../components/ModalUI";

const DEV_PROXY_BASE = "/api";
const PROD_API_BASE = "https://zangon-web-api.azurewebsites.net";
const API_BASE = process.env.NODE_ENV === "development" ? DEV_PROXY_BASE : PROD_API_BASE;
const SESSION_KEY = "zangon_session";

function safeJsonParse(text) {
  try { return applyBillingEnumText(JSON.parse(text)); } catch { return null; }
}

function getSession() {
  try { return JSON.parse(localStorage.getItem(SESSION_KEY) || "null"); } catch { return null; }
}

function setSession(session) {
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
}

export function getAccessToken() { return getSession()?.accessToken || ""; }
export function getRefreshToken() { return getSession()?.refreshToken || ""; }
export function saveLoginSession(payload) {
  setSession(payload);
  localStorage.setItem("auth_token", payload?.accessToken || "");
}

async function fetchWithTimeout(url, options = {}, timeoutMs = 60000) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err?.name === "AbortError") throw new Error("Request timed out. Try again.");
    throw err;
  } finally { clearTimeout(id); }
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token.");
  const res = await fetchWithTimeout(`${API_BASE}/auth/refresh`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  const text = await res.text();
  const json = text ? safeJsonParse(text) : null;
  if (!res.ok) throw new Error(json?.message || "Token refresh failed.");
  const next = { ...(getSession() || {}), ...(json?.data ?? json ?? {}) };
  setSession(next);
  localStorage.setItem("auth_token", next?.accessToken || "");
  return next;
}

async function authFetch(url, options = {}) {
  let token = getAccessToken();
  const headers = {
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
    ...(options.headers || {}),
  };
  if (options.body && typeof options.body === "string" && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  let res = await fetchWithTimeout(url, { ...options, headers });
  if (res.status === 401) {
    try {
      const renewed = await refreshAccessToken();
      token = renewed.accessToken;
      headers.Authorization = `Bearer ${token}`;
      res = await fetchWithTimeout(url, { ...options, headers });
    } catch {
      clearSession();
      window.location.href = "/sign-in";
      throw new Error("Session expired. Please sign in again.");
    }
  }
  return res;
}

function parseArrayData(json) {
  if (!json) return [];
  const raw = json.data;
  if (raw && typeof raw === "object" && Array.isArray(raw.data)) return raw.data;
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    const parsed = safeJsonParse(raw);
    if (Array.isArray(parsed)) return parsed;
    if (parsed && Array.isArray(parsed.data)) return parsed.data;
  }
  if (Array.isArray(json)) return json;
  return [];
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function getInitials(name = "") {
  const parts = name.trim().split(" ");
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return (parts[0]?.[0] || "?").toUpperCase();
}

async function uploadAdminImage(imageFile, adminId) {
  if (!imageFile || !adminId) {
    console.error("Upload aborted: Missing file or adminId", { hasFile: !!imageFile, adminId });
    return null;
  }
  try {
    const formData = new FormData();
    formData.append("file", imageFile);
    const res = await authFetch(`${API_BASE}/admins/${adminId}/uploads`, {
      method: "POST",
      headers: {},
      body: formData,
    });
    const text = await res.text();
    const json = safeJsonParse(text);
    if (!res.ok) {
      console.error("Backend rejected upload:", json?.message || res.status, json);
      return null;
    }
    if (!json || typeof json.data !== "string") {
      console.error("Unexpected upload response structure:", json);
      return null;
    }
    return json.data;
  } catch (err) {
    console.error("uploadAdminImage error:", err);
    return null;
  }
}

const PAGE_SIZE = 30;

function AddTenantModal({ modalRef, estateId, adminId, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [buildings, setBuildings] = useState([]);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isCustomTitle, setIsCustomTitle] = useState(false);

  const blankForm = {
    isCorporate: false, buildingId: "", rentAmount: "",
    title: "", firstName: "", lastName: "", email: "", phoneNumber: "", occupation: "",
    corporateName: "", contactPersonFirstName: "",
    contactPersonLastName: "", contactPersonEmail: "", contactPersonPhoneNumber: "",
    contactPersonJobTitle: "", billingAddress: "", notes: "",
  };
  const [form, setForm] = useState(blankForm);

  useEffect(() => {
    if (!form.isCorporate) {
      const fName = form.firstName.trim().toLowerCase().replace(/\s+/g, '');
      const lName = form.lastName.trim().toLowerCase().replace(/\s+/g, '');
      if (fName || lName) {
        setForm(prev => ({ ...prev, email: `${fName}${lName}@zangon.com` }));
      } else {
        setForm(prev => ({ ...prev, email: "" }));
      }
    } else {
      const cpFName = form.contactPersonFirstName.trim().toLowerCase().replace(/\s+/g, '');
      const cpLName = form.contactPersonLastName.trim().toLowerCase().replace(/\s+/g, '');
      const corpName = form.corporateName.trim().toLowerCase().replace(/[^a-z0-9]/g, '');
      let autoEmail = "";
      if (cpFName || cpLName) {
        autoEmail = `${cpFName}${cpLName}@zangon.com`;
      } else if (corpName) {
        autoEmail = `${corpName}@zangon.com`;
      }
      setForm(prev => ({ ...prev, contactPersonEmail: autoEmail, email: autoEmail }));
    }
  }, [form.firstName, form.lastName, form.corporateName, form.contactPersonFirstName, form.contactPersonLastName, form.isCorporate]);

  const fetchedEstateId = useRef(null);
  useEffect(() => {
    const el = modalRef.current;
    if (!el) return;
    const onShow = () => {
      if (!estateId || fetchedEstateId.current === estateId) return;
      fetchedEstateId.current = estateId;
      setLoadingBuildings(true);
      authFetch(`${API_BASE}/estates/${estateId}/buildings/assignable`)
        .then(r => r.ok ? r.json() : null)
        .then(json => setBuildings(parseArrayData(json)))
        .catch(() => {})
        .finally(() => setLoadingBuildings(false));
    };
    el.addEventListener('show.bs.modal', onShow);
    return () => el.removeEventListener('show.bs.modal', onShow);
  }, [estateId, modalRef]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const e = {};
    if (form.isCorporate) {
      if (!form.corporateName.trim()) e.corporateName = "Required";
      if (!form.contactPersonEmail.trim() || !/\S+@\S+\.\S+/.test(form.contactPersonEmail)) {
        e.contactPersonEmail = "Valid email required";
      }
      if (!form.contactPersonPhoneNumber.trim()) e.contactPersonPhoneNumber = "Required";
    } else {
      if (!form.firstName.trim()) e.firstName = "Required";
      if (!form.lastName.trim()) e.lastName = "Required";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
      if (!form.phoneNumber.trim()) e.phoneNumber = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const resetAndClose = () => {
    setErrors({});
    setImagePreview(null);
    setImageFile(null);
    setIsCustomTitle(false);
    setForm(blankForm);
    setStep(1);
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
    onClose?.();
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      let imageUrl = null;
      if (imageFile && adminId) {
        setUploadingImage(true);
        imageUrl = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
        if (!imageUrl) {
          toast("Photo upload failed — tenant will be created without a photo.", { icon: "⚠️" });
        }
      }

      const cpFirstName = form.contactPersonFirstName.trim() || "Contact";
      const cpLastName = form.contactPersonLastName.trim() || "Person";

      const payload = form.isCorporate
        ? {
            name: form.corporateName.trim(),
            firstName: form.corporateName.trim(), 
            lastName: "",
            email: form.contactPersonEmail.trim(),
            phoneNumber: form.contactPersonPhoneNumber.trim(),
            sector: form.occupation.trim() || "",
            isCorporateTenant: true,
            ...(imageUrl && { imageUrl }),
            // The backend is rejecting the request because this block was missing:
            contactPerson: {
              firstName: cpFirstName,
              lastName: cpLastName,
              email: form.contactPersonEmail.trim(),
              phoneNumber: form.contactPersonPhoneNumber.trim(),
              jobTitle: form.contactPersonJobTitle?.trim() || "",
            }
          }
        : {
            estateId,
            title: form.title.trim(),
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phoneNumber: form.phoneNumber.trim(),
            occupation: form.occupation.trim(),
            isCorporateTenant: false,
            ...(imageUrl && { imageUrl }),
          };

      const endpoint = form.isCorporate ? "/tenants/corporate" : "/tenants/individual";
      const res = await authFetch(`${API_BASE}${endpoint}`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const json = safeJsonParse(await res.text());

      if (!res.ok) {
        let msg = "Failed to create tenant.";
        if (res.status === 400 && json?.errors) {
          const k = Object.keys(json.errors)[0];
          msg = `${k}: ${json.errors[k][0]}`;
        } else if (json?.message) {
          msg = json.message;
        }
        throw new Error(msg);
      }

      const newTenantId = json?.data?.id || json?.data?.tenantId || json?.data;

      // Handle building assignment
      if (form.buildingId && newTenantId) {
        const assignRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${newTenantId}/assign-buildings`, {
          method: "POST",
          body: JSON.stringify({
            estatesId: estateId,
            buildingIds: [form.buildingId],
            rentSchedule: 0,
          }),
        });
        if (!assignRes.ok) {
          toast("Tenant created, but building assignment failed. Please re-assign from their profile.", { icon: "⚠️" });
        }
      }

      // Handle separated contact person POST for Corporate Tenants
      if (form.isCorporate && newTenantId) {
        const cpRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${newTenantId}/contact-persons`, {
          method: "POST",
          body: JSON.stringify({
            firstName: cpFirstName,
            lastName: cpLastName,
            email: form.contactPersonEmail.trim(),
            phoneNumber: form.contactPersonPhoneNumber.trim(),
            jobTitle: form.contactPersonJobTitle?.trim() || "",
          })
        });
        
        if (!cpRes.ok) {
          toast("Company Tenant created, but linking the Contact Person failed.", { icon: "⚠️" });
        }
      }

      const successName = form.isCorporate ? form.corporateName : `${form.firstName} ${form.lastName}`;
      toast.success(`${successName} added successfully!`);
      resetAndClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message || "Failed to create tenant.");
    } finally {
      setLoading(false);
      setUploadingImage(false);
    }
  };

  const titleIsPreset = TITLE_OPTIONS.includes(form.title);
  const inputCls = (key) => `d6m-input ${errors[key] ? "is-invalid" : ""}`;
  const busy = loading || uploadingImage;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-profile-user"
      title="Add New Tenant"
      onClose={resetAndClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={resetAndClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={busy}>
            {uploadingImage ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Uploading photo…</>
            ) : loading ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Creating…</>
            ) : (
              "Create Tenant"
            )}
          </button>
        </>
      }
    >
      <div className="d6m-seg mb-5">
        {[{ label: "Individual", icon: "ki-user", val: false }, { label: "Corporate", icon: "ki-office-bag", val: true }].map(({ label, icon, val }) => (
          <button
            key={label}
            type="button"
            className={form.isCorporate === val ? "active" : ""}
            onClick={() => { setForm((p) => ({ ...p, isCorporate: val })); setErrors({}); }}
          >
            <i className={`ki-outline ${icon}`}></i>
            {label}
          </button>
        ))}
      </div>

      <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">{form.isCorporate ? "Company logo" : "Tenant photo"}</div>
          <div className="d6m-muted">Optional · PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change" : "Upload"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      {form.isCorporate ? (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Company</span></div>
          <div className="row g-4">
            <Field className="col-12" label="Company name" required error={errors.corporateName}>
              <input className={inputCls("corporateName")} placeholder="Acme Corp" value={form.corporateName} onChange={e => setForm(p => ({ ...p, corporateName: e.target.value }))} />
            </Field>
          </div>
          <div className="d6m-section-head mt-6"><span>Primary contact person</span></div>
          <div className="row g-4">
            <Field className="col-6" label="First name">
              <input className={inputCls("contactPersonFirstName")} placeholder="Jane" value={form.contactPersonFirstName} onChange={e => setForm(p => ({ ...p, contactPersonFirstName: e.target.value }))} />
            </Field>
            <Field className="col-6" label="Last name">
              <input className={inputCls("contactPersonLastName")} placeholder="Doe" value={form.contactPersonLastName} onChange={e => setForm(p => ({ ...p, contactPersonLastName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Phone number" required error={errors.contactPersonPhoneNumber}>
              <PhoneInput
                value={form.contactPersonPhoneNumber}
                invalid={!!errors.contactPersonPhoneNumber}
                onChange={(v) => setForm(p => ({ ...p, contactPersonPhoneNumber: v }))}
              />
            </Field>
            <Field className="col-12" label="Contact email" required hint="Generated from the contact's name." error={errors.contactPersonEmail}>
              <input type="email" className={`${inputCls("contactPersonEmail")} is-readonly`} placeholder="janedoe@zangon.com" value={form.contactPersonEmail} readOnly />
            </Field>
            <Field className="col-6" label="Job title">
              <input className="d6m-input" placeholder="e.g. CEO" value={form.contactPersonJobTitle} onChange={e => setForm(p => ({ ...p, contactPersonJobTitle: e.target.value }))} />
            </Field>
            <Field className="col-6" label="Sector / notes">
              <input className="d6m-input" placeholder="e.g. Technology" value={form.occupation} onChange={e => setForm(p => ({ ...p, occupation: e.target.value }))} />
            </Field>
          </div>
        </div>
      ) : (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Personal details</span></div>
          <div className="row g-4">
            <Field className="col-4" label="Title">
              <select
                className="d6m-input"
                value={isCustomTitle ? "OTHERS" : titleIsPreset ? form.title : form.title ? "OTHERS" : ""}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "OTHERS") setIsCustomTitle(true);
                  else { setIsCustomTitle(false); setForm(p => ({ ...p, title: val })); }
                }}
              >
                <option value="">None</option>
                {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                <option value="OTHERS">Other…</option>
              </select>
            </Field>
            <Field className="col-8" label="First name" required error={errors.firstName}>
              <input className={inputCls("firstName")} placeholder="John" value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} />
            </Field>
            {(isCustomTitle || (form.title && !titleIsPreset)) && (
              <Field className="col-12" label="Custom title">
                <input className="d6m-input" placeholder="e.g. Justice" value={titleIsPreset ? "" : form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
              </Field>
            )}
            <Field className="col-12" label="Last name" required error={errors.lastName}>
              <input className={inputCls("lastName")} placeholder="Doe" value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Phone number" required error={errors.phoneNumber}>
              <PhoneInput value={form.phoneNumber} invalid={!!errors.phoneNumber} onChange={(v) => setForm(p => ({ ...p, phoneNumber: v }))} />
            </Field>
            <Field className="col-12" label="Login email" required hint="Generated from their name." error={errors.email}>
              <input type="email" className={`${inputCls("email")} is-readonly`} placeholder="johndoe@zangon.com" value={form.email} readOnly />
            </Field>
            <Field className="col-12" label="Occupation">
              <input className="d6m-input" placeholder="e.g. Software Engineer" value={form.occupation} onChange={e => setForm(p => ({ ...p, occupation: e.target.value }))} />
            </Field>
          </div>

          <div className="d6m-section-head mt-6"><span>Assign building <span className="d6m-muted">(optional)</span></span></div>
          <div className="row g-4">
            <Field className="col-12">
              <select className="d6m-input" value={form.buildingId} onChange={e => setForm(p => ({ ...p, buildingId: e.target.value }))} disabled={loadingBuildings}>
                <option value="">{loadingBuildings ? "Loading buildings..." : "Select a building..."}</option>
                {buildings.map(b => (
                  <option key={b.id} value={b.id}>{b.number} — {b.phaseName} ({b.typeName})</option>
                ))}
              </select>
            </Field>
            {form.buildingId && (
              <Field className="col-12" label="Rent amount (₦)">
                <input type="number" className="d6m-input" placeholder="e.g. 1500000" value={form.rentAmount} onChange={e => setForm(p => ({ ...p, rentAmount: e.target.value }))} />
              </Field>
            )}
          </div>
        </div>
      )}
    </ModalShell>
  );
}

function EditTenantModal({ modalRef, tenant, estateId, adminId, onSuccess }) {
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);
  const [isCustomTitle, setIsCustomTitle] = useState(false);
  const [form, setForm] = useState({
    title: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    occupation: "",
    corporateName: ""
  });
  const [contactPerson, setContactPerson] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    jobTitle: ""
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const interval = setInterval(() => {
      const session = getSession();
      if (!session?.accessTokenExpiresAt) return;
      const expiresAt = new Date(session.accessTokenExpiresAt).getTime();
      const timeUntilExpiry = expiresAt - Date.now();
      if (timeUntilExpiry > 0 && timeUntilExpiry < 3 * 60 * 1000) {
        refreshAccessToken().catch((err) => {
          console.warn("Background refresh failed, waiting for authFetch fallback.", err);
        });
      }
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (tenant) {
      const isCorporate = tenant.isCorporateTenant || tenant.isCorp;
      setForm({
        title: tenant.title || "",
        firstName: tenant.firstName || "",
        lastName: tenant.lastName || "",
        email: tenant.email || "",
        phoneNumber: tenant.phoneNumber || tenant.phone || "",
        occupation: tenant.occupation || "nil",
        corporateName: isCorporate ? (tenant.companyName || tenant.name || `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim()) : ""
      });
      setImagePreview(tenant.imageUrl || tenant.avatar || null);
      setImageFile(null);
      setErrors({});

      if (isCorporate) {
        const tenantId = tenant.tenantId || tenant.id;
        authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons`)
          .then(r => r.ok ? r.json() : null)
          .then(json => {
            const data = parseArrayData(json);
            if (data && data.length > 0) {
              setContactPerson({
                firstName: data[0].firstName || "",
                lastName: data[0].lastName || "",
                email: data[0].email || "",
                phoneNumber: data[0].phoneNumber || "",
                jobTitle: data[0].jobTitle || "",
              });
            } else {
              setContactPerson({ firstName: "", lastName: "", email: "", phoneNumber: "", jobTitle: "" });
            }
          })
          .catch(() => {
            setContactPerson({ firstName: "", lastName: "", email: "", phoneNumber: "", jobTitle: "" });
          });
      }
    }
  }, [tenant, estateId]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Image must be under 5MB."); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
    const e = {};
    if (isCorporate) {
      if (!form.corporateName.trim()) e.corporateName = "Required";
      if (contactPerson.email.trim() && !/\S+@\S+\.\S+/.test(contactPerson.email)) {
        e.contactPersonEmail = "Valid email required";
      }
    } else {
      if (!form.firstName.trim()) e.firstName = "Required";
      if (!form.lastName.trim()) e.lastName = "Required";
      if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = "Valid email required";
      if (!form.phoneNumber.trim()) e.phoneNumber = "Required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleClose = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
    setSaving(true);
    try {
      let imageUrl = tenant?.imageUrl || tenant?.avatar || undefined;
      if (imageFile && adminId) {
        setUploadingImage(true);
        const uploaded = await uploadAdminImage(imageFile, adminId);
        setUploadingImage(false);
        if (uploaded) {
          imageUrl = uploaded;
        } else {
          toast("Photo upload failed — other changes will still be saved.", { icon: "⚠️" });
        }
      }

      const tenantId = tenant?.tenantId || tenant?.id;

      // Update tenant details
      const payload = isCorporate
        ? {
            name: form.corporateName.trim(),
            firstName: form.corporateName.trim(),
            lastName: "",
            email: contactPerson.email.trim(),
            phoneNumber: contactPerson.phoneNumber.trim(),
            occupation: form.occupation.trim() || "nil",
            ...(imageUrl && { imageUrl }),
          }
        : {
            title: form.title,
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            email: form.email.trim(),
            phoneNumber: form.phoneNumber.trim(),
            occupation: form.occupation.trim() || "nil",
            ...(imageUrl && { imageUrl }),
          };

      const res = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to update tenant.");

      // Update contact person if corporate
      if (isCorporate) {
        const cpRes = await authFetch(`${API_BASE}/estates/${estateId}/tenants/${tenantId}/contact-persons`, {
          method: "POST",
          body: JSON.stringify({
            firstName: contactPerson.firstName.trim(),
            lastName: contactPerson.lastName.trim(),
            email: contactPerson.email.trim(),
            phoneNumber: contactPerson.phoneNumber.trim(),
            jobTitle: contactPerson.jobTitle?.trim() || "",
          })
        });
        if (!cpRes.ok) {
          toast("Tenant updated, but linking the updated Contact Person failed.", { icon: "⚠️" });
        }
      }

      toast.success("Tenant updated successfully.");
      handleClose();
      onSuccess?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const isCorporate = tenant?.isCorporateTenant || tenant?.isCorp;
  const titleIsPreset = TITLE_OPTIONS.includes(form.title);
  const inputCls = (key) => `d6m-input ${errors[key] ? "is-invalid" : ""}`;
  const busy = saving || uploadingImage;

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-profile-user"
      title="Edit Tenant"
      onClose={handleClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={handleClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleSubmit} disabled={busy}>
            {uploadingImage ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Uploading…</>
            ) : saving ? (
              <><span className="spinner-border spinner-border-sm me-2"></span>Saving…</>
            ) : (
              "Save Changes"
            )}
          </button>
        </>
      }
    >
      <div className="d6m-upload">
        <button type="button" className="d6m-upload-preview" onClick={() => fileInputRef.current?.click()} aria-label="Upload photo">
          {imagePreview ? <img src={imagePreview} alt="" /> : <i className="ki-outline ki-picture"></i>}
        </button>
        <div className="flex-grow-1 min-w-0">
          <div className="d6m-v">{isCorporate ? "Company logo" : "Tenant photo"}</div>
          <div className="d6m-muted">Optional · PNG or JPG, up to 5MB</div>
        </div>
        <div className="d-flex gap-2">
          {imagePreview && (
            <button type="button" className="d6m-link-btn" onClick={() => { setImageFile(null); setImagePreview(null); }}>Remove</button>
          )}
          <button type="button" className="btn btn-light d6-mbtn d6-mbtn-sm" onClick={() => fileInputRef.current?.click()}>
            {imagePreview ? "Change" : "Upload"}
          </button>
        </div>
        <input ref={fileInputRef} type="file" accept="image/*" className="d-none" onChange={handleImageSelect} />
      </div>

      {isCorporate ? (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Company</span></div>
          <div className="row g-4">
            <Field className="col-12" label="Company name" required error={errors.corporateName}>
              <input className={inputCls("corporateName")} placeholder="Acme Corp" value={form.corporateName} onChange={e => setForm(p => ({ ...p, corporateName: e.target.value }))} />
            </Field>
          </div>
          <div className="d6m-section-head mt-6"><span>Primary contact person</span></div>
          <div className="row g-4">
            <Field className="col-6" label="First name">
              <input className="d6m-input" placeholder="Jane" value={contactPerson.firstName} onChange={e => setContactPerson(p => ({ ...p, firstName: e.target.value }))} />
            </Field>
            <Field className="col-6" label="Last name">
              <input className="d6m-input" placeholder="Doe" value={contactPerson.lastName} onChange={e => setContactPerson(p => ({ ...p, lastName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Contact email" error={errors.contactPersonEmail}>
              <input type="email" className={inputCls("contactPersonEmail")} placeholder="jane@acmecorp.com" value={contactPerson.email} onChange={e => setContactPerson(p => ({ ...p, email: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Contact phone">
              <input
                type="tel"
                inputMode="numeric"
                maxLength={11}
                className="d6m-input"
                placeholder="08012345678"
                value={contactPerson.phoneNumber}
                onChange={(e) => {
                  let value = e.target.value.replace(/\D/g, "");
                  if (value.length > 0 && value[0] !== "0") value = "0" + value;
                  value = value.slice(0, 11);
                  setContactPerson(p => ({ ...p, phoneNumber: value }));
                }}
              />
            </Field>
            <Field className="col-6" label="Job title">
              <input className="d6m-input" placeholder="e.g. CEO" value={contactPerson.jobTitle} onChange={e => setContactPerson(p => ({ ...p, jobTitle: e.target.value }))} />
            </Field>
            <Field className="col-6" label="Sector / notes">
              <input className="d6m-input" placeholder="e.g. Technology" value={form.occupation} onChange={e => setForm(p => ({ ...p, occupation: e.target.value }))} />
            </Field>
          </div>
        </div>
      ) : (
        <div className="d6m-section">
          <div className="d6m-section-head"><span>Personal details</span></div>
          <div className="row g-4">
            <Field className="col-4" label="Title">
              <select
                className="d6m-input"
                value={isCustomTitle ? "OTHERS" : titleIsPreset ? form.title : form.title ? "OTHERS" : ""}
                onChange={e => {
                  const val = e.target.value;
                  if (val === "OTHERS") setIsCustomTitle(true);
                  else { setIsCustomTitle(false); setForm(p => ({ ...p, title: val })); }
                }}
              >
                <option value="">None</option>
                {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                <option value="OTHERS">Other…</option>
              </select>
            </Field>
            <Field className="col-8" label="First name" required error={errors.firstName}>
              <input className={inputCls("firstName")} placeholder="John" value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} />
            </Field>
            {(isCustomTitle || (form.title && !titleIsPreset)) && (
              <Field className="col-12" label="Custom title">
                <input className="d6m-input" placeholder="e.g. Justice" value={titleIsPreset ? "" : form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
              </Field>
            )}
            <Field className="col-12" label="Last name" required error={errors.lastName}>
              <input className={inputCls("lastName")} placeholder="Doe" value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Email address" required error={errors.email}>
              <input type="email" className={inputCls("email")} placeholder="johndoe@zangon.com" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            </Field>
            <Field className="col-12" label="Phone number" required error={errors.phoneNumber}>
              <PhoneInput value={form.phoneNumber} invalid={!!errors.phoneNumber} onChange={(v) => setForm(p => ({ ...p, phoneNumber: v }))} />
            </Field>
            <Field className="col-12" label="Occupation">
              <input className="d6m-input" placeholder="e.g. Software Engineer" value={form.occupation} onChange={e => setForm(p => ({ ...p, occupation: e.target.value }))} />
            </Field>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

const StatCardSkeleton = () => (
  <div className="col-12 col-md-6 col-xl-3">
    <div className="p-6 h-100 border-end border-white border-opacity-25 placeholder-glow">
      <div className="d-flex align-items-center">
        <div className="symbol symbol-45px me-4">
          <div className="placeholder rounded-circle w-45px h-45px bg-white bg-opacity-20"></div>
        </div>
        <div className="flex-grow-1 d-flex flex-column gap-2">
          <div className="placeholder rounded w-75 bg-white bg-opacity-40 h-15px"></div>
          <div className="placeholder rounded w-50 bg-white bg-opacity-75 h-30px"></div>
          <div className="placeholder rounded w-60 bg-white bg-opacity-25 h-10px"></div>
        </div>
      </div>
    </div>
  </div>
);

const TableSkeleton = () => (
  <div className="table-responsive placeholder-glow">
    <table className="table table-row-dashed align-middle gs-0 gy-4">
      <thead>
        <tr className="fw-bold text-gray-600 fs-7">
          <th className="min-w-300px">Tenant</th>
          <th className="min-w-150px">Contact</th>
          <th className="min-w-100px">Building</th>
          <th className="min-w-100px">Rent</th>
          <th className="min-w-100px">Presence</th>
          <th className="min-w-130px text-end">Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5].map(n => (
          <tr key={n}>
            <td>
              <div className="d-flex align-items-center">
                <div className="placeholder rounded-circle bg-secondary me-3 w-45px h-45px"></div>
                <div className="d-flex flex-column gap-2">
                  <div className="placeholder rounded bg-secondary w-125px h-15px"></div>
                  <div className="placeholder rounded bg-secondary bg-opacity-50 w-75px h-15px"></div>
                </div>
              </div>
            </td>
            <td>
              <div className="d-flex flex-column gap-2">
                <div className="placeholder rounded bg-secondary w-100px h-15px"></div>
                <div className="placeholder rounded bg-secondary bg-opacity-50 w-125px h-10px"></div>
              </div>
            </td>
            <td>
              <div className="d-flex flex-column gap-2">
                <div className="placeholder rounded bg-secondary w-50px h-15px"></div>
                <div className="placeholder rounded bg-secondary bg-opacity-50 w-75px h-10px"></div>
              </div>
            </td>
            <td><div className="placeholder rounded-pill bg-secondary w-75px h-25px"></div></td>
            <td><div className="placeholder rounded-pill bg-secondary w-75px h-25px"></div></td>
            <td className="text-end">
              <div className="d-flex gap-2 justify-content-end">
                <div className="placeholder rounded bg-secondary w-35px h-35px"></div>
                <div className="placeholder rounded bg-secondary w-35px h-35px"></div>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const GridSkeleton = () => (
  <div className="row g-6 placeholder-glow">
    {[1, 2, 3, 4, 5, 6].map(n => (
      <div className="col-12 col-sm-6 col-xl-4" key={n}>
        <div className="card h-100">
          <div className="card-header border-0 pt-9">
            <div className="card-title m-0">
              <div className="placeholder rounded-circle bg-secondary w-50px h-50px"></div>
            </div>
            <div className="card-toolbar d-flex align-items-end gap-2">
              <div className="placeholder rounded-pill bg-secondary w-75px h-25px"></div>
              <div className="placeholder rounded-pill bg-secondary w-75px h-25px"></div>
            </div>
          </div>
          <div className="card-body p-9 text-center">
            <div className="placeholder rounded-circle bg-secondary mb-5 mx-auto w-100px h-100px"></div>
            <div className="placeholder rounded bg-secondary mb-2 mx-auto w-75 h-25px"></div>
            <div className="placeholder rounded bg-secondary bg-opacity-50 mb-7 mx-auto w-50 h-15px"></div>
            <div className="d-flex flex-wrap mb-5 justify-content-center">
              <div className="border border-gray-300 border-dashed rounded min-w-140px py-3 px-4 me-7 mb-3">
                <div className="placeholder rounded bg-secondary mb-1 mx-auto w-75px h-20px"></div>
                <div className="placeholder rounded bg-secondary bg-opacity-50 mx-auto w-50px h-15px"></div>
              </div>
              <div className="border border-gray-300 border-dashed rounded min-w-140px py-3 px-4 mb-3">
                <div className="placeholder rounded bg-secondary mb-1 mx-auto w-75px h-20px"></div>
                <div className="placeholder rounded bg-secondary bg-opacity-50 mx-auto w-50px h-15px"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

export default function TenantManagement() {
  const navigate = useNavigate();
  const session = getSession();
  const estateId = session?.estateId || "";
  const adminId = session?.userId || "";

  const [pageReady, setPageReady] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [buildingFilter, setBuildingFilter] = useState("all");
  const [presenceFilter, setPresenceFilter] = useState("all");
  const [rentFilter, setRentFilter] = useState("all");
  const [activationFilter, setActivationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("houseNumber");
  const [viewMode, setViewMode] = useState("card");
  const [page, setPage] = useState(1);
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTenant, setSelectedTenant] = useState(null);

  const [staticStats, setStaticStats] = useState({ total: 0, corporate: 0, individual: 0, withDue: 0, hasLoaded: false });

  const addTenantModalRef = useRef(null);
  const editTenantModalRef = useRef(null);
  const downloadTenantModalRef = useRef(null);

  useEffect(() => {
    const body = document.body;
    body.id = "kt_app_body";
    body.classList.add("app-default");
    body.setAttribute("data-kt-app-header-fixed-mobile", "true");
    body.setAttribute("data-kt-app-toolbar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-enabled", "true");
    body.setAttribute("data-kt-app-sidebar-fixed", "true");
    body.setAttribute("data-kt-app-sidebar-push-header", "true");
    body.setAttribute("data-kt-app-sidebar-push-toolbar", "true");
    body.setAttribute("data-kt-app-sidebar-push-footer", "true");

    const html = document.documentElement;
    const saved = localStorage.getItem("kt_theme_mode") || "light";
    html.setAttribute("data-bs-theme", saved);

    const onClick = (e) => {
      const el = e.target.closest('[data-kt-element="mode"]');
      if (!el) return;
      const mode = el.getAttribute("data-kt-value");
      if (mode) {
        if (window.KTThemeMode?.setMode) window.KTThemeMode.setMode(mode);
        else { html.setAttribute("data-bs-theme", mode); localStorage.setItem("kt_theme_mode", mode); }
      }
    };
    document.addEventListener("click", onClick);

    const init = () => {
      window.KTThemeMode?.init?.();
      window.KTComponents?.init?.();
      window.KTMenu?.createInstances?.();
      window.KTDrawer?.createInstances?.();
      window.KTScroll?.createInstances?.();
      window.KTScrollTop?.createInstances?.();
    };
    init();
    const t = setTimeout(init, 0);
    return () => { document.removeEventListener("click", onClick); clearTimeout(t); };
  }, []);

  const fetchTenants = useCallback(async () => {
    if (!estateId) { setIsLoading(false); setPageReady(true); return; }
    setIsLoading(true);
    try {
      const [resResult, activationResult, upcomingRentResult] = await Promise.allSettled([
        authFetch(`${API_BASE}/estates/${estateId}/tenants/with-dependents?pageIndex=0&pageSize=1000`),
        // Account activation is estate-admin only.
        isPhaseAdmin() ? Promise.resolve(null) : authFetch(`${API_BASE}/estates/${estateId}/account-activation-status?pageIndex=0&pageSize=1000`),
        authFetch(`${API_BASE}/estates/${estateId}/tenants/upcoming-rent?daysAhead=30&pageIndex=0&pageSize=1000`)
      ]);

      const res = resResult.status === "fulfilled" && resResult.value.ok ? resResult.value : null;
      const json = res ? safeJsonParse(await res.text()) : null;
      let raw = parseArrayData(json);

      const activationMap = new Map();
      if (activationResult.status === "fulfilled" && activationResult.value?.ok) {
        const actJson = safeJsonParse(await activationResult.value.text());
        const accountsList = actJson?.data?.accounts?.data || actJson?.data?.accounts || parseArrayData(actJson);
        if (Array.isArray(accountsList)) {
          accountsList.map(normalizeActivationAccount).forEach(acc => {
            if (acc.id) activationMap.set(String(acc.id).toLowerCase(), acc);
            if (acc.email) activationMap.set(String(acc.email).trim().toLowerCase(), acc);
            const digits = String(acc.phoneNumber || "").replace(/\D/g, "");
            if (digits) {
              activationMap.set(`phone_${digits}`, acc);
              if (digits.length >= 10) activationMap.set(`phone_${digits.slice(-10)}`, acc);
            }
          });
        }
      }

      const upcomingRentMap = new Map();
      if (upcomingRentResult.status === "fulfilled" && upcomingRentResult.value?.ok) {
        const upcomingJson = safeJsonParse(await upcomingRentResult.value.text());
        const upcomingItems = upcomingJson?.data?.data || upcomingJson?.data || parseArrayData(upcomingJson);
        if (Array.isArray(upcomingItems)) {
          upcomingItems.forEach(item => {
            const tid = item.tenantId || item.id || item.requesterId;
            if (tid) upcomingRentMap.set(String(tid).toLowerCase(), item);
            const emailKey = String(item.email || "").trim().toLowerCase();
            if (emailKey) upcomingRentMap.set(emailKey, item);
          });
        }
      }

      const enriched = raw.map((t, idx) => {
        const tid = t.tenantId || t.id || "";
        const isCorp = t.isCorporateTenant || false;
        const titleStr = t.title ? `${t.title} ` : "";
        const firstName = t.firstName || "";
        const lastName = t.lastName || "";

        const name = isCorp
          ? `${firstName} ${lastName}`.trim() || "Corporate Tenant"
          : `${titleStr}${firstName} ${lastName}`.trim() || "Unknown";

        const buildings = t.currentBuildings || [];
        const buildingNumbers = buildings.map(b => b.number || "");

        const houseLabel = buildings.length > 0
          ? (buildings.length > 5
              ? buildings.slice(0, 5).map(b => `${b.phaseName || ""} — ${b.number || ""}`.trim()).join(", ") + ` (+${buildings.length - 5} more)`
              : buildings.map(b => `${b.phaseName || ""} — ${b.number || ""}`.trim()).join(", ")
            )
          : "Unassigned";

        const avatar = t.imageUrl || null;

        const emailKey = String(t.email || "").trim().toLowerCase();
        const phoneDigits = String(t.phoneNumber || t.phone || "").replace(/\D/g, "");
        const idKey = String(tid).toLowerCase();

        const actInfo = activationMap.get(idKey)
          || (emailKey ? activationMap.get(emailKey) : null)
          || (phoneDigits ? (activationMap.get(`phone_${phoneDigits}`) || (phoneDigits.length >= 10 ? activationMap.get(`phone_${phoneDigits.slice(-10)}`) : null)) : null);

        const isActivated = actInfo
          ? String(actInfo.activationStatus || "").toLowerCase() === "activated"
          : String(t.activationStatus || "").toLowerCase() === "activated";

        const activationStatus = actInfo?.activationStatus || (isActivated ? "Activated" : "Pending");
        const hasAssignedHouse = Array.isArray(buildings) && buildings.length > 0;

        let avatarClass = "bg-light-success text-success";
        if (!hasAssignedHouse) {
          avatarClass = "bg-light-danger text-danger";
        } else if (!isActivated) {
          avatarClass = "bg-light-warning text-warning";
        }

        const upcomingData = upcomingRentMap.get(idKey) || (emailKey ? upcomingRentMap.get(emailKey) : null);
        const isOwing = !!upcomingData;
        const rentStatus = isOwing ? "Due" : "Up to date";

        const rentInfo = upcomingData?.upcomingRentBuildings?.[0] || {};
        let totalRentVal = 0;
        if (rentInfo.totalAmount !== undefined && rentInfo.totalAmount !== null && Number(rentInfo.totalAmount) > 0) {
          totalRentVal = Number(rentInfo.totalAmount);
        } else if (rentInfo.totalAmountDue !== undefined && rentInfo.totalAmountDue !== null && Number(rentInfo.totalAmountDue) > 0) {
          totalRentVal = Number(rentInfo.totalAmountDue);
        } else if (upcomingData?.totalAmountDue !== undefined && upcomingData?.totalAmountDue !== null && Number(upcomingData.totalAmountDue) > 0) {
          totalRentVal = Number(upcomingData.totalAmountDue);
        } else if (Array.isArray(rentInfo.components) && rentInfo.components.length > 0) {
          totalRentVal = rentInfo.components.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);
        } else if (rentInfo.rentAmount !== undefined && rentInfo.rentAmount !== null && Number(rentInfo.rentAmount) > 0) {
          totalRentVal = Number(rentInfo.rentAmount);
        } else {
          totalRentVal = 10000000;
        }

        return {
          id: tid, firstName, lastName, name, initials: getInitials(name),
          title: t.title || "",
          email: t.email || "",
          phone: t.phoneNumber || t.phone || "",
          occupation: t.occupation || "",
          isCorp, avatar,
          avatarClass,
          buildings: buildings,
          buildingNumbers: buildingNumbers,
          houseLabel: houseLabel,
          house: houseLabel,
          buildingNumber: buildings[0]?.number || "",
          phaseName: buildings[0]?.phaseName || "",
          rentStatus,
          rentAmount: isOwing ? `₦${totalRentVal.toLocaleString()}` : "—",
          dependentCount: t.dependents ? t.dependents.length : 0,
          dependents: t.dependents || [],
          contactPersons: t.contactPersons || (t.contactPerson ? [t.contactPerson] : []),
          dateJoined: fmtDate(t.createdAt || t.createdDate),
          outstandingCount: isOwing ? 1 : 0,
          inEstate: t.inEstate ?? true,
          isActive: isActivated,
          activationStatus,
        };
      });

      setTenants(enriched);
      setTotalCount(json?.data?.totalCount || enriched.length);

      setStaticStats({
        total: json?.data?.totalCount || enriched.length,
        corporate: enriched.filter(t => t.isCorp).length,
        individual: enriched.filter(t => !t.isCorp).length,
        withDue: enriched.filter(t => t.rentStatus === "Due" || t.rentStatus === "Owing").length,
        hasLoaded: true 
      });
    } catch {
      toast.error("Failed to load tenants.");
    } finally {
      setIsLoading(false);
      setPageReady(true);
    }
  }, [estateId]);

  const fetchedState = useRef({ estateId: null });
  useEffect(() => {
    const s = fetchedState.current;
    if (s.estateId === estateId) return;
    s.estateId = estateId;
    fetchTenants();
  }, [fetchTenants, estateId]);

  const uniquePhases = useMemo(() => {
    const phases = new Set();
    tenants.forEach(t => {
      if (t.phaseName) phases.add(t.phaseName);
      if (t.buildings) {
        t.buildings.forEach(b => { if (b.phaseName) phases.add(b.phaseName); });
      }
    });
    return Array.from(phases).sort();
  }, [tenants]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qDigits = q.replace(/\D/g, "");

    const matchesPhone = (phoneStr) => {
      if (!phoneStr) return false;
      const lowerPhone = String(phoneStr).toLowerCase();
      if (lowerPhone.includes(q)) return true;
      if (qDigits.length >= 3) {
        const cleanPhone = lowerPhone.replace(/\D/g, "");
        const localPhone = cleanPhone.replace(/^234/, "0");
        const noZeroPhone = cleanPhone.replace(/^(234|0)/, "");
        const cleanQ = qDigits;
        const localQ = qDigits.replace(/^234/, "0");
        const noZeroQ = qDigits.replace(/^(234|0)/, "");

        if (cleanPhone.includes(cleanQ) || cleanPhone.includes(localQ) || cleanPhone.includes(noZeroQ)) return true;
        if (localPhone.includes(cleanQ) || localPhone.includes(localQ) || localPhone.includes(noZeroQ)) return true;
        if (noZeroPhone.includes(cleanQ) || noZeroPhone.includes(localQ) || noZeroPhone.includes(noZeroQ)) return true;
      }
      return false;
    };

    const list = tenants.filter(t => {
      let matchSearch = !q;
      if (q) {
        // Primary Tenant details
        const firstName = (t.firstName || "").toLowerCase();
        const lastName = (t.lastName || "").toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        const mainName = (t.name || "").toLowerCase();
        const email = (t.email || "").toLowerCase();
        const occupation = (t.occupation || "").toLowerCase();
        const title = (t.title || "").toLowerCase();
        const houseLabel = (t.houseLabel || "").toLowerCase();

        matchSearch =
          firstName.includes(q) ||
          lastName.includes(q) ||
          fullName.includes(q) ||
          mainName.includes(q) ||
          email.includes(q) ||
          occupation.includes(q) ||
          title.includes(q) ||
          houseLabel.includes(q) ||
          (t.buildingNumbers && t.buildingNumbers.some(b => String(b).toLowerCase().includes(q))) ||
          (t.phaseName && String(t.phaseName).toLowerCase().includes(q)) ||
          matchesPhone(t.phone);

        // Dependents (e.g. Elizabeth Ichie, Elisabethebenyi3@gmail.com, 09011562961)
        if (!matchSearch && t.dependents && Array.isArray(t.dependents)) {
          matchSearch = t.dependents.some(d => {
            const dFirst = (d.firstName || "").toLowerCase();
            const dLast = (d.lastName || "").toLowerCase();
            const dFull = `${dFirst} ${dLast}`.trim();
            const dEmail = (d.email || "").toLowerCase();
            const dPhone = d.phoneNumber || d.phone || "";
            return (
              dFirst.includes(q) ||
              dLast.includes(q) ||
              dFull.includes(q) ||
              dEmail.includes(q) ||
              matchesPhone(dPhone)
            );
          });
        }

        // Contact Persons (if Corporate Tenant)
        if (!matchSearch && t.contactPersons && Array.isArray(t.contactPersons)) {
          matchSearch = t.contactPersons.some(cp => {
            const cpFirst = (cp.firstName || "").toLowerCase();
            const cpLast = (cp.lastName || "").toLowerCase();
            const cpFull = `${cpFirst} ${cpLast}`.trim();
            const cpEmail = (cp.email || "").toLowerCase();
            const cpPhone = cp.phoneNumber || cp.phone || "";
            return (
              cpFirst.includes(q) ||
              cpLast.includes(q) ||
              cpFull.includes(q) ||
              cpEmail.includes(q) ||
              matchesPhone(cpPhone)
            );
          });
        }
      }

      const matchType = typeFilter === "all" || (typeFilter === "individual" && !t.isCorp) || (typeFilter === "corporate" && t.isCorp);
      const matchBuilding = buildingFilter === "all" || t.buildingNumbers.includes(buildingFilter);
      const matchPresence = presenceFilter === "all" || (presenceFilter === "in" && t.inEstate) || (presenceFilter === "out" && !t.inEstate);
      const matchRent = rentFilter === "all" || (rentFilter === "due" && t.rentStatus === "Due") || (rentFilter === "nodue" && t.rentStatus !== "Due");
      const matchPhase = phaseFilter === "all" || t.phaseName === phaseFilter || (t.buildings && t.buildings.some(b => b.phaseName === phaseFilter));
      const matchActivation = activationFilter === "all" ||
        (activationFilter === "activated" && (t.isActive === true || String(t.activationStatus).toLowerCase().includes("activated"))) ||
        (activationFilter === "pending" && (t.isActive === false || String(t.activationStatus).toLowerCase().includes("pending"))) ||
        (activationFilter === "deactivated" && (String(t.activationStatus).toLowerCase().includes("deactivated") || String(t.activationStatus).toLowerCase().includes("inactive")));
      return matchSearch && matchType && matchBuilding && matchPresence && matchRent && matchPhase && matchActivation;
    });

    if (sortBy === "phase") {
      return [...list].sort((a, b) => {
        const phaseA = String(a.buildings?.[0]?.phaseName || a.phaseName || "");
        const phaseB = String(b.buildings?.[0]?.phaseName || b.phaseName || "");
        if (!phaseA && !phaseB) return 0;
        if (!phaseA) return 1;
        if (!phaseB) return -1;
        const compPhase = phaseA.localeCompare(phaseB, undefined, { numeric: true, sensitivity: "base" });
        if (compPhase !== 0) return compPhase;
        const numA = String(a.buildings?.[0]?.number || a.buildingNumbers?.[0] || a.houseLabel || "");
        const numB = String(b.buildings?.[0]?.number || b.buildingNumbers?.[0] || b.houseLabel || "");
        return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
      });
    }

    // Default sort by houseNumber
    return [...list].sort((a, b) => {
      const numA = String(a.buildings?.[0]?.number || a.buildingNumbers?.[0] || a.houseLabel || "");
      const numB = String(b.buildings?.[0]?.number || b.buildingNumbers?.[0] || b.houseLabel || "");
      if (!numA && !numB) return 0;
      if (!numA) return 1;
      if (!numB) return -1;
      return numA.localeCompare(numB, undefined, { numeric: true, sensitivity: "base" });
    });
  }, [tenants, search, typeFilter, buildingFilter, presenceFilter, rentFilter, phaseFilter, activationFilter, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, buildingFilter, presenceFilter, rentFilter, phaseFilter, activationFilter, sortBy]);

  const openModal = (ref) => {
    if (window.bootstrap?.Modal && ref.current) {
      window.bootstrap.Modal.getOrCreateInstance(ref.current, { backdrop: "static" }).show();
    }
  };

  const statItems = [
    { icon: "ki-people", label: "Total Tenants", value: staticStats.total, sub: "Active accounts" },
    { icon: "ki-user", label: "Individual Tenants", value: staticStats.individual, sub: "Individual tenants" },
    { icon: "ki-office-bag", label: "Corporate Tenants", value: staticStats.corporate, sub: "Corporate tenants" },
    { icon: "ki-information-2", label: "Rent Overdue", value: staticStats.withDue, sub: "Outstanding rent" },
  ];

  return (
    <>
      <Dashboard6Styles />

      <PagePreloader visible={!pageReady} />
      <div className="d-flex flex-column flex-root app-root" id="kt_app_root">
        <div className="app-page flex-column flex-column-fluid" id="kt_app_page">

          <div id="kt_app_header" className="app-header" data-kt-sticky="true" data-kt-sticky-activate="{default: false, lg: true}" data-kt-sticky-name="app-header-sticky" data-kt-sticky-offset="{default: false, lg: '300px'}">
            <div className="app-container container-fluid d-flex flex-stack align-items-center my-6" id="kt_app_header_container">
              <div className="d-flex align-items-center d-block d-lg-none ms-n3">
                <div className="btn btn-icon btn-active-color-primary w-35px h-35px me-2" id="kt_app_sidebar_mobile_toggle">
                  <i className="ki-outline ki-abstract-14 fs-2"></i>
                </div>
                <Link to="/dashboard"><img alt="Logo" src="/assets/media/zangon-fav.png" className="h-30px" /></Link>
              </div>
              <PageHeader title="Tenant Management">
                <button type="button" className="btn btn-light d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => openModal(downloadTenantModalRef)}>
                  <i className="ki-outline ki-file-down fs-6 me-sm-2"></i>
                  <span className="d-none d-sm-inline">Download Tenant Log</span>
                </button>
                <button type="button" className="btn btn-success d-flex flex-center h-35px rounded-pill px-4 fs-8 fw-bold" onClick={() => openModal(addTenantModalRef)}>
                  <i className="ki-outline ki-plus fs-6 me-sm-2 text-white"></i>
                  <span className="d-none d-sm-inline">Add New Tenant</span>
                </button>
              </PageHeader>
            </div>
          </div>

          <div className="app-wrapper flex-column flex-row-fluid" id="kt_app_wrapper">
            <Sidebar collapsible />
            <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
              <div className="d-flex flex-column flex-column-fluid">
                <div id="kt_app_content" className="app-content flex-column-fluid">
                  <div id="kt_app_content_container" className="app-container container-fluid">

                    <div className="d6-kpi-brand p-6 mb-5">
                      {isLoading && !staticStats.hasLoaded ? (
                        <div className="row g-0">
                          {[1, 2, 3, 4].map(n => <StatCardSkeleton key={n} />)}
                        </div>
                      ) : (
                        <div className="row g-4 g-xl-0 align-items-center position-relative" style={{ zIndex: 2 }}>
                          {statItems.map((item, i) => (
                            <div key={item.label} className="col-12 col-sm-6 col-xl-3">
                              <div className={`px-4 py-2 h-100 ${i < 3 ? "border-end-xl border-white border-opacity-20" : ""}`}>
                                <div className="d-flex align-items-center">
                                  <div className="symbol symbol-45px me-4 flex-shrink-0">
                                    <span className="symbol-label bg-white bg-opacity-15 rounded-3">
                                      <i className={`ki-outline ${item.icon} fs-2 text-white`}></i>
                                    </span>
                                  </div>
                                  <div className="flex-grow-1 min-w-0">
                                    <div className="text-white text-opacity-80 fs-8 fw-semibold">{item.label}</div>
                                    <div className="d6-num text-white my-0.5">{item.value}</div>
                                    <div className="text-white text-opacity-70 fs-9">{item.sub}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="d6-card p-6 mb-5">
                      <CardHead title="Tenants Log" subtitle="Search & filter tenants." className="mb-5">
                        <div className="position-relative">
                          <i className="ki-outline ki-magnifier fs-5 position-absolute top-50 start-0 translate-middle-y ms-3 text-gray-400"></i>
                          <input
                            className="d6m-input ps-10"
                            style={{ width: 260, minHeight: 40, height: 40, borderRadius: 999 }}
                            placeholder="Search by name or house..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                          />
                        </div>
                        <select className="d6m-input" style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }} value={phaseFilter} onChange={e => setPhaseFilter(e.target.value)}>
                          <option value="all">All Phases</option>
                          {uniquePhases.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                        {!isPhaseAdmin() && (
                          <select className="d6m-input" style={{ width: 170, minHeight: 40, height: 40, borderRadius: 999 }} value={activationFilter} onChange={e => setActivationFilter(e.target.value)}>
                            <option value="all">All Activation</option>
                            <option value="activated">Activated</option>
                            <option value="pending">Pending Activation</option>
                            <option value="deactivated">Deactivated</option>
                          </select>
                        )}
                        <select className="d6m-input" style={{ width: 150, minHeight: 40, height: 40, borderRadius: 999 }} value={rentFilter} onChange={e => setRentFilter(e.target.value)}>
                          <option value="all">All Rent Status</option>
                          <option value="due">Due Rent</option>
                          <option value="nodue">No Due Rent</option>
                        </select>
                        <div className="d6-seg">
                          <button type="button" className={`fs-8 fw-bold ${viewMode === "list" ? "active" : ""}`} onClick={() => setViewMode("list")} title="List view">
                            <i className="ki-outline ki-row-horizontal fs-5 m-0"></i>
                          </button>
                          <button type="button" className={`fs-8 fw-bold ${viewMode === "card" ? "active" : ""}`} onClick={() => setViewMode("card")} title="Card view">
                            <i className="ki-outline ki-element-11 fs-5 m-0"></i>
                          </button>
                        </div>
                      </CardHead>

                      <div>
                        {isLoading ? (
                          viewMode === "list" ? <TableSkeleton /> : <GridSkeleton />
                        ) : filtered.length === 0 ? (
                          <div className="d6m-empty py-12">
                            <i className="ki-outline ki-people fs-3x text-gray-300 d-block mb-3"></i>
                            <div className="d6-title text-gray-900 mb-1">No tenants found.</div>
                            <div className="fs-8 text-gray-500">Try adjusting your search or filters.</div>
                          </div>
                        ) : viewMode === "list" ? (
                          <div className="d-flex flex-column gap-3">
                            {paged.map(t => {
                              const houseLabel = t.houseLabel || (t.buildings?.[0]?.number ? `House ${t.buildings[0].number}` : "Resident");
                              const phoneNum = t.phone || t.phoneNumber || "";
                              const isRentDue = t.rentStatus === "Due" || t.rentStatus === "Owing";

                              return (
                                <Link
                                  key={t.id}
                                  to={`/tenants/${t.id}`}
                                  className={`d6-card d6-list-card d-flex align-items-center mb-2 py-4 px-5 text-decoration-none ${isRentDue ? "border-hover-danger" : "border-hover-success"} position-relative overflow-hidden cursor-pointer transition-all`}
                                  style={{ minHeight: 90 }}
                                >
                                  <div className={`position-absolute top-0 start-0 h-100 ${isRentDue ? "bg-danger" : "bg-success"} w-4px`}></div>

                                  <div className="d-flex align-items-center me-4" style={{ width: "420px", flexShrink: 0 }}>
                                    <Avatar src={t.avatar} name={t.name} size={48} />
                                    <div className="d-flex flex-column overflow-hidden min-w-0 ms-3">
                                      <div className="d-flex align-items-center gap-2 mb-0.5 flex-wrap">
                                        <span className="fs-6 fw-bold text-gray-900 text-truncate">{t.name || "Tenant"}</span>
                                        <span className="badge bg-light-success text-success fs-9 fw-semibold rounded-pill px-2.5 py-0.5">{t.isCorp ? "Corporate" : "Individual"}</span>
                                        {phoneNum && <span className="badge bg-light text-gray-700 fs-9 fw-semibold rounded-pill px-2 py-0.5">{phoneNum}</span>}
                                      </div>
                                      <div className="text-gray-500 fs-8 text-truncate" title={houseLabel}>{houseLabel}</div>
                                    </div>
                                  </div>

                                  <div className="d-flex align-items-center gap-5 text-end ms-auto flex-shrink-0">
                                    <div className="d-none d-sm-block text-center">
                                      <div className="text-gray-400 fs-9 mb-0.5">Joined</div>
                                      <div className="fw-semibold text-gray-900 fs-8">{t.dateJoined || "—"}</div>
                                    </div>
                                    <div className="d-none d-sm-block text-center">
                                      <div className="text-gray-400 fs-9 mb-0.5">Dependants</div>
                                      <div className="fw-semibold text-gray-900 fs-8">{t.dependentCount || 0}</div>
                                    </div>
                                    {isRentDue && (
                                      <div className="d-none d-sm-block text-center">
                                        <div className="text-gray-400 fs-9 mb-0.5">Rent Due</div>
                                        <div className="fw-bold text-danger fs-8">{t.rentAmount}</div>
                                      </div>
                                    )}
                                    <div className="text-center">
                                      <StatusChip color={isRentDue ? "#DC2626" : "#16A34A"}>{isRentDue ? "Owing" : "Up to date"}</StatusChip>
                                    </div>
                                    <span className="d6-icon-btn" title="View profile"><i className="ki-outline ki-arrow-up-right fs-5"></i></span>
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="row g-5">
                            {paged.map(t => {
                              const isRentDue = t.rentStatus === "Due" || t.rentStatus === "Owing";
                              return (
                                <div className="col-12 col-md-6 col-xl-4" key={t.id}>
                                  <Link
                                    className={`d6-card h-100 d-flex flex-column text-decoration-none ${isRentDue ? "border-hover-danger" : "border-hover-success"} transition-all p-5 position-relative overflow-hidden`}
                                    to={`/tenants/${t.id}`}
                                  >
                                    <div className="d-flex align-items-start justify-content-between gap-2 mb-4">
                                      <Avatar src={t.avatar} name={t.name} size={56} />
                                      <StatusChip color={isRentDue ? "#DC2626" : "#16A34A"}>{isRentDue ? "Owing" : "Up to date"}</StatusChip>
                                    </div>
                                    <h4 className="d6-title text-gray-900 mb-1 text-truncate">{t.name}</h4>
                                    <span className="d6m-muted fs-8 text-truncate mb-4" title={t.houseLabel}>{t.houseLabel}</span>
                                    <div className="d6m-kv mt-auto">
                                      <div className="d6m-kv-cell">
                                        <span className="d6m-k">Rent Due</span>
                                        <span className={`d6m-v ${isRentDue ? "text-danger" : ""}`}>{t.rentAmount}</span>
                                      </div>
                                      <div className="d6m-kv-cell">
                                        <span className="d6m-k">{t.buildings && t.buildings.length > 1 ? "Properties" : "Dependants"}</span>
                                        <span className="d6m-v">{t.buildings && t.buildings.length > 1 ? t.buildings.length : t.dependentCount}</span>
                                      </div>
                                    </div>
                                  </Link>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {filtered.length > PAGE_SIZE && !isLoading && (
                          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-6 pt-5 border-top border-gray-200">
                            <div className="text-gray-500 fs-8">
                              Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} tenants
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <button type="button" className="d6-icon-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
                                <i className="ki-outline ki-left fs-5"></i>
                              </button>
                              {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, idx, arr) => {
                                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                  acc.push(p);
                                  return acc;
                                }, [])
                                .map((p, idx) =>
                                  p === "..." ? (
                                    <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">…</span>
                                  ) : (
                                    <button key={p} type="button" className={`btn btn-sm d6-mbtn ${page === p ? "btn-success" : "btn-light"}`} style={{ height: 32, padding: "0 12px" }} onClick={() => setPage(p)}>
                                      {p}
                                    </button>
                                  )
                                )}
                              <button type="button" className="d6-icon-btn" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
                                <i className="ki-outline ki-right fs-5"></i>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                  </div>
                </div>
              </div>

              <div id="kt_app_footer" className="app-footer">
                <div className="app-container container-fluid d-flex justify-content-center align-items-center py-3">
                  <div className="text-gray-900 text-center">
                    <span className="text-muted fw-semibold me-1">2026&copy;</span>
                    <a href="https://pixels.com.ng/" target="_blank" rel="noreferrer" className="text-gray-800 text-hover-primary">Powered by Pixels Solutions Limited</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div id="kt_scrolltop" className="scrolltop bg-success" data-kt-scrolltop="true">
            <i className="ki-outline ki-arrow-up"></i>
          </div>
        </div>
      </div>

      <AddTenantModal
        modalRef={addTenantModalRef}
        estateId={estateId}
        adminId={adminId}
        onClose={() => {}}
        onSuccess={fetchTenants}
      />
      <EditTenantModal
        modalRef={editTenantModalRef}
        tenant={selectedTenant}
        estateId={estateId}
        adminId={adminId}
        onSuccess={fetchTenants}
      />
      <DownloadTenantModal
        modalRef={downloadTenantModalRef}
        tenants={filtered}
      />
    </>
  );
}

function toTitleCase(str) {
  if (!str) return '';
  return String(str).replace(/\w\S*/g, word =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
  );
}

function DownloadTenantModal({ modalRef, tenants = [] }) {
  // 1. Cleaned up options (Removed occupation, firstName, lastName, fullName)
  const [options, setOptions] = useState({
    title: true,
    tenantName: true,
    email: true,
    phoneNumber: true,
    rentStatus: true,
    currentBuildings: true,
    phase: true,
    dependents: true,
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = () => {
    const activeHeaders = Object.keys(options).filter(k => options[k]);
    
    if (activeHeaders.length === 0) {
      toast.error("Please select at least one column to download.");
      return;
    }
    if (tenants.length === 0) {
      toast.error("No records to export matching active filters.");
      return;
    }

    setDownloading(true);
    try {
      // 2. Simplified Mapping
      const headerMapping = {
        title: "Title",
        tenantName: "Tenant Name",
        email: "Email Address",
        phoneNumber: "Phone Number",
        rentStatus: "Rent Status",
        currentBuildings: "Buildings",
        phase: "Phase",
        dependents: "Dependents"
      };

      const nonCorpList = tenants.filter(t => !t.isCorp);
      const corpList = tenants.filter(t => t.isCorp);

      // 3. Clean row builder
      const buildRow = (item, isCorp) => {
        const row = {};
        activeHeaders.forEach(key => {
          const columnName = headerMapping[key];
          
          switch (key) {
            case 'title':           
              row[columnName] = isCorp ? '' : toTitleCase(item.title); 
              break;
            case 'tenantName':       
              row[columnName] = toTitleCase(item.name || ''); 
              break;
            case 'email':           
              row[columnName] = item.email || ''; 
              break;
            case 'phoneNumber':     
              row[columnName] = item.phone || ''; 
              break;
            case 'rentStatus':     
              row[columnName] = item.rentStatus || ''; 
              break;
            case 'currentBuildings':
              row[columnName] = item.buildings?.map(b => b.number).join(', ') || ''; 
              break;
            case 'phase':           
              row[columnName] = item.buildings?.map(b => b.phaseName).filter(Boolean).join(', ') || ''; 
              break;
            case 'dependents':      
              row[columnName] = item.dependents?.map(d =>
              `${toTitleCase(d.firstName || '')} ${toTitleCase(d.lastName || '')} (${d.relationship || 'Dependent'})`.trim()
            ).join(', ') || 'None'; 
              break;
            default:                
              row[columnName] = '';
          }
        });
        return row;
      };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(nonCorpList.map(t => buildRow(t, false))), "Non-Corporate Tenants");
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(corpList.map(t => buildRow(t, true))), "Corporate Tenants");
      XLSX.writeFile(wb, `tenants_log_${new Date().toISOString().split("T")[0]}.xlsx`);

      toast.success(`Download started — ${tenants.length} tenant${tenants.length !== 1 ? 's' : ''} exported.`);
      if (window.bootstrap?.Modal && modalRef.current) {
        window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
      }
    } catch (err) {
      toast.error("Failed to generate download.");
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const closeModal = () => {
    if (window.bootstrap?.Modal && modalRef.current) {
      window.bootstrap.Modal.getInstance(modalRef.current)?.hide();
    }
  };
  const allSelected = Object.values(options).every(Boolean);

  return (
    <ModalShell
      modalRef={modalRef}
      icon="ki-file-down"
      title="Download Tenant Log"
      onClose={closeModal}
      closeDisabled={downloading}
      width={640}
      footer={
        <>
          <button type="button" className="btn btn-light d6-mbtn" onClick={closeModal} disabled={downloading}>Cancel</button>
          <button type="button" className="btn btn-success d6-mbtn" onClick={handleDownload} disabled={downloading}>
            {downloading ? <><span className="spinner-border spinner-border-sm me-2"></span>Exporting…</> : "Download Export"}
          </button>
        </>
      }
    >
      <div className="d6m-note mt-0 mb-5">
        <i className="ki-outline ki-information-5"></i>
        <span>Individual and corporate tenants are saved on separate sheets in the same file.</span>
      </div>
      <div className="d6m-section-head">
        <span>Columns to include</span>
        <button
          type="button"
          className="d6m-link-btn"
          onClick={() => setOptions(Object.fromEntries(Object.keys(options).map((k) => [k, !allSelected])))}
        >
          {allSelected ? "Clear all" : "Select all"}
        </button>
      </div>
      <div className="d6m-list">
        {Object.keys(options).map((key) => (
          <label key={key} className={`d6m-list-row d6m-check-row ${options[key] ? "active" : ""}`}>
            <input
              type="checkbox"
              checked={options[key]}
              onChange={(e) => setOptions({ ...options, [key]: e.target.checked })}
              disabled={downloading}
            />
            <span className="d6m-check" aria-hidden="true">
              <svg viewBox="0 0 24 24"><path d="M5 12.5 L10 17.5 L19 7.5" /></svg>
            </span>
            <span className="min-w-0 flex-grow-1">
              <span className="d6m-v d-block text-capitalize">{key.replace(/([A-Z])/g, " $1").trim()}</span>
            </span>
          </label>
        ))}
      </div>
    </ModalShell>
  );
}
